/**
 * Client-side helpers for the share flow.
 *
 * Flow:
 *   1. requestShare(payload) → POST /api/share/generate
 *   2. On success → open Twitter intent with text + shareUrl
 *   3. Twitter scrapes shareUrl → reads OG meta tags → embeds image
 *
 * Tweet text is composed here (random pick from a few variants) so
 * each share rolls a different opener.
 */

export interface ArenaResultClientPayload {
  type: 'arena-result';
  position: number;
  totalPlayers: number;
  tokensRaw: string;
  poolRaw: string;
  lbPoints: number;
  symbol: string;
  decimals: number;
  /** Optional USD-micro value of `tokensRaw` at share time (computed
   *  client-side from /api/payments/quote's femtoUsdcPerRaw). If
   *  present, the card renders a "≈ $X.XX" subline; otherwise omits. */
  usdMicroValue?: string;
}

export interface StandingsClientRow {
  position: number;
  name: string;
  score: number;
  kills: number;
  isMe?: boolean;
}

export interface StandingsClientPayload {
  type: 'standings';
  arenaIdShort: string;
  rows: StandingsClientRow[];
  poolRaw: string;
  symbol: string;
  decimals: number;
}

export interface TournamentResultClientPayload {
  type: 'tournament-result';
  runScore: number;
  totalScore: number;
  kills: number;
  died: boolean;
}

export type ShareClientPayload =
  | ArenaResultClientPayload
  | StandingsClientPayload
  | TournamentResultClientPayload;

const ARENA_RESULT_TEXTS = [
  'Snaked my way to #{position} of #{total} in @PumpMySnake 🐍 Walked away with {tokens} {symbol}. Your turn →',
  'Top #{position} in the arena 🐍 +{tokens} {symbol} in my pocket. Play @PumpMySnake →',
  'Survived. Banked. Extracted. +{tokens} {symbol} from @PumpMySnake →',
];

const STANDINGS_TEXTS = [
  "Just played a match of @PumpMySnake 🐍 Here's how it shook out →",
  'Final standings from my last @PumpMySnake match 🏆 →',
];

const TOURNAMENT_TEXTS = [
  'Scored {score} in the @PumpMySnake tournament 🏆🐍 Top it and win 200 USDC in $PENGXBT — entry is free →',
  '{score} this run in the @PumpMySnake tournament 🐍 One life, free entry, 200 USDC in $PENGXBT up for grabs. Your move →',
  'Tournament run: {score} points in @PumpMySnake 🏆 200 USDC in $PENGXBT for the top snakes. Think you can beat it? →',
];

function pickRandom<T>(arr: readonly T[]): T {
  return arr[Math.floor(Math.random() * arr.length)]!;
}

function fmtAmount(rawStr: string, decimals: number): string {
  try {
    const raw = BigInt(rawStr);
    const human = Number(raw) / 10 ** decimals;
    if (human >= 1) return human.toLocaleString('en-US', { maximumFractionDigits: 2 });
    if (human >= 0.01) return human.toFixed(4).replace(/0+$/, '').replace(/\.$/, '');
    return human.toFixed(6).replace(/0+$/, '').replace(/\.$/, '');
  } catch {
    return '0';
  }
}

function composeText(payload: ShareClientPayload): string {
  if (payload.type === 'arena-result') {
    return pickRandom(ARENA_RESULT_TEXTS)
      .replace('#{position}', String(payload.position))
      .replace('#{total}', String(payload.totalPlayers))
      .replace('{tokens}', fmtAmount(payload.tokensRaw, payload.decimals))
      .replace('{symbol}', payload.symbol);
  }
  if (payload.type === 'tournament-result') {
    return pickRandom(TOURNAMENT_TEXTS)
      .replace('{score}', payload.totalScore.toLocaleString());
  }
  return pickRandom(STANDINGS_TEXTS);
}

/** Request the share image + share-page URL from the API, then open
 *  the Twitter intent popup with the prepared text + URL.
 *
 *  Throws on network / API failure so the caller can surface a
 *  toast / inline error. Cancelled popups (popup blocker) silently
 *  succeed — the URL was generated, the user can copy/share later. */
export async function requestShare(payload: ShareClientPayload): Promise<{ shareUrl: string; imageUrl: string }> {
  const resp = await fetch('/api/share/generate', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!resp.ok) {
    const data = await resp.json().catch(() => ({}));
    throw new Error(data?.error || `share failed (${resp.status})`);
  }
  const data = await resp.json() as { shareUrl: string; imageUrl: string };
  if (!data.shareUrl) throw new Error('share endpoint returned no shareUrl');

  const text = composeText(payload);
  const twitterUrl =
    'https://twitter.com/intent/tweet'
    + '?text=' + encodeURIComponent(text)
    + '&url=' + encodeURIComponent(data.shareUrl);

  // Popup may be blocked by the browser (no user gesture) — fall back to
  // navigating this tab only if it actually failed to open.
  //
  // IMPORTANT: do NOT pass `noopener`/`noreferrer` here. With `noopener`,
  // window.open returns null even on success, so the `!win` fallback would
  // ALSO run and the compose window would open TWICE (popup + this tab).
  const win = window.open(twitterUrl, '_blank', 'width=550,height=420');
  if (!win) {
    console.warn('[share] popup blocked; fallback to direct navigation');
    window.location.href = twitterUrl;
  }

  return data;
}
